import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { getGuestSession } from '@/services/guestSession'

/**
 * Definicja tras aplikacji NHEX.
 * Lazy-loading dla wszystkich widoków oprócz lobby (domyślny).
 */
const routes: RouteRecordRaw[] = [
  /* ==================== PUBLICZNE ==================== */
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { guest: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: { guest: true },
  },
  {
    path: '/password-reset',
    name: 'password-reset',
    component: () => import('@/views/PasswordResetView.vue'),
    meta: { guest: true },
  },
  {
    path: '/password-reset/confirm/:token',
    name: 'password-reset-confirm',
    component: () => import('@/views/PasswordResetConfirmView.vue'),
    meta: { guest: true },
  },
  {
    path: '/forgot-username',
    name: 'forgot-username',
    component: () => import('@/views/ForgotUsernameView.vue'),
    meta: { guest: true },
  },
  {
    path: '/activate/:token',
    name: 'activate',
    component: () => import('@/views/ActivationView.vue'),
    meta: { guest: true },
  },
  {
    path: '/support',
    name: 'support',
    component: () => import('@/views/SupportView.vue'),
  },
  {
    path: '/tutorial',
    name: 'tutorial',
    component: () => import('@/views/TutorialView.vue'),
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/TermsView.vue'),
  },

  /* ==================== WYMAGAJĄ LOGOWANIA ==================== */
  {
    path: '/',
    name: 'lobby',
    component: () => import('@/views/LobbyView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/password-change',
    name: 'password-change',
    component: () => import('@/views/PasswordChangeView.vue'),
    meta: { requiresAuth: true },
  },

  /* --- ARMIE --- */
  {
    path: '/armies',
    name: 'armies',
    component: () => import('@/views/army/ArmyListView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/armies/:id',
    name: 'army-detail',
    component: () => import('@/views/army/ArmyDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/armies/:id/tokens',
    name: 'army-tokens',
    component: () => import('@/views/army/TokensView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/armies/:id/tokens/:tokenId',
    name: 'army-token-detail',
    component: () => import('@/views/army/TokenDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/armies/:id/resources',
    name: 'army-resources',
    component: () => import('@/views/army/ResourcesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/armies/:id/info',
    name: 'army-info',
    component: () => import('@/views/army/ArmyInfoView.vue'),
    meta: { requiresAuth: true },
  },

  /* --- STOŁY --- */
  {
    path: '/tables/:id',
    name: 'table-detail',
    component: () => import('@/views/TableDetailView.vue'),
    meta: { requiresAuth: true },
  },

  /* --- ADMIN --- */
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/admin/AdminView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/boards/:id/calibrate',
    name: 'admin-board-calibrator',
    component: () => import('@/views/admin/BoardCalibratorView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/pub-reqs',
    name: 'pub-reqs',
    component: () => import('@/views/admin/PubReqsView.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
  },

  /* ==================== GRA ==================== */
  {
    path: '/:id',
    name: 'play',
    component: () => import('@/views/PlayView.vue'),
    meta: { requiresAuth: true },
  },

  /* Zaproszenia linkowe — przekierowuje do gry */
  {
    path: '/invite/:id',
    name: 'invite',
    component: () => import('@/views/InviteView.vue'),
  },

  /* ==================== 404 ==================== */
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition || { top: 0 }
  },
})

/* Guard nawigacji — sprawdza autoryzację i role */
router.beforeEach(async (to) => {
  const auth = useAuthStore()

  /* Przywróć sesję z tokenu (jeśli istnieje) */
  if (!auth.initialized) {
    await auth.initialize()
  }

  /* Strony tylko dla gości (login, register) — zalogowani idą do lobby */
  if (to.meta.guest && auth.isAuthenticated) {
    return { name: 'lobby' }
  }

  /* Strony wymagające logowania */
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    if (to.name === 'play') {
      const accessId = String(to.params.id || '')
      if (getGuestSession(accessId)) return true
      if (/^[\w-]{10}$/.test(accessId)) {
        return { name: 'invite', params: { id: accessId } }
      }
    }
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  /* Strony wymagające roli admin */
  if (to.meta.requiresAdmin && !auth.isAdmin) {
    return { name: 'lobby' }
  }

  /* Strony wymagające roli staff (moderator+) */
  if (to.meta.requiresStaff && !auth.isStaff) {
    return { name: 'lobby' }
  }
})

export default router
